import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Star, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="relative hidden lg:block bg-[#0B3B2E] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1602943543714-cf535b048440?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="tea garden"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3B2E] via-[#0B3B2E]/70 to-[#0B3B2E]/30" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 grid place-items-center">
              <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-full w-full object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]" data-testid="login-hero-logo" />
            </div>
            <div>
              <div className="font-display text-2xl text-[#C9A34A]">Prime Gold Tea</div>
              <div className="text-xs uppercase tracking-[0.25em] text-[#F7F2E7]/70">Catalogue Studio</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-5xl text-[#F7F2E7] leading-tight">
              Craft premium tea<br />sample catalogues.
            </h1>
            <p className="mt-4 max-w-md text-[#F7F2E7]/70">
              Build WhatsApp-ready catalogues with tasting reports, liquor images and verified credentials — in minutes.
            </p>
            <div className="mt-6 flex items-center gap-4 text-[#E3C77C]">
              <div className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-4 w-4 fill-[#C9A34A] text-[#C9A34A]" />)}
                <span className="ml-1 text-sm text-[#F7F2E7]">4.8/5 Google Rating</span>
              </div>
              <span className="text-sm text-[#F7F2E7]/60 font-mono-id">TB|LC|DI-10237</span>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background pgt-paper">
        <form onSubmit={submit} className="w-full max-w-sm pgt-fade-up" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-12 w-12 grid place-items-center">
              <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-full w-full object-contain" data-testid="login-mobile-logo" />
            </div>
            <span className="font-display text-2xl text-[#0B3B2E]">Prime Gold Tea</span>
          </div>
          <h2 className="font-display text-3xl text-[#0B3B2E]">Welcome back</h2>
          <p className="text-sm text-[#14513D]/70 mt-1 mb-6">Sign in to your catalogue studio.</p>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@primegoldtea.net" required data-testid="login-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-[#0B3B2E] hover:bg-[#14513D] h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </div>

          <div className="mt-6 rounded-lg border border-[#C9A34A]/30 bg-[#FAF7F0] px-4 py-3 text-xs text-[#14513D]">
            <div className="font-semibold mb-1">Demo accounts</div>
            <div>Admin — admin@primegoldtea.net / Admin@2026</div>
            <div>Sales — sales@primegoldtea.net / Sales@2026</div>
          </div>
        </form>
      </div>
    </div>
  );
}
